import type { ComponentChildren } from 'preact';
import { useRef } from 'preact/hooks';
import { Ref } from 'preact';
import { forwardRef, useImperativeHandle } from 'preact/compat';
import { TabBarContext } from '../UNSAFE_TabBarCommon';
import { useTabBar } from './useTabBar';
import { styles } from './themes/TabBarMixedStyles.css';

type UseTabBarOptions = Parameters<typeof useTabBar>[0];

/**
 * Component props expected to be pased to **TabBarMixed**.
 *
 * @see {@link TabBarMixed}
 */
type TabBarMixedProps<K extends string | number> = {
  /**
   * Component children that will be rendered within the **TabBarMixed**
   * root element.
   *
   * It is expected that **TabBarLayout** and **ConveyorBelt** elements are
   * provided. The same **TabBarItem**, **RemovableTabBarItem**,
   * and **OverflowTabBarItem** elements should also be used.
   */
  children?: ComponentChildren;
  /**
   * Callback fired when a tab item is removed.
   */
  onRemove?: UseTabBarOptions['onRemove'];
  /**
   * Callback fired when a tab item is selected.
   */
  onSelect?: UseTabBarOptions['onSelect'];
  /**
   * The item key of the selected tab item.
   */
  selection?: K;
  /**
   * The height of the the tab bar.
   *
   * @default "md"
   */
  size?: UseTabBarOptions['size'];
};

export type FocusableHandle = {
  focus: () => void;
  blur: () => void;
};

/**
 * A navigation component that enables horizontal navigation between distinct content with a mixture of static and dynamic tabs.
 *
 * @param {TabBarMixedProps} props TabBarMixed component props.
 * @returns {JSX.Element} TabBarMixed component element.
 */
export const TabBarMixed = forwardRef(
  <K extends string | number>(
    props: TabBarMixedProps<K>,
    focusHandleRef?: Ref<FocusableHandle>
  ) => {
    const { children, onRemove, onSelect, selection, size } = props;

    const rootRef = useRef<HTMLDivElement>(null);

    const { rootProps, tabBarContext } = useTabBar({
      class: styles.tabBarBase,
      edge: 'bottom',
      onRemove,
      onSelect,
      ref: rootRef,
      selection,
      size,
      children
    });

    //Allows to call focus on rootRef without having to expose it
    useImperativeHandle(focusHandleRef!, () => ({
      focus: () => {
        if (rootRef.current) {
          rootRef.current.focus();
        }
      },
      blur: () => {
        if (rootRef.current) {
          rootRef.current.blur();
        }
      }
    }));

    return (
      <TabBarContext.Provider value={tabBarContext}>
        <div {...rootProps}>{children}</div>
      </TabBarContext.Provider>
    );
  }
);
